import { normalizePlaybookKey } from '@/utils';

export const EVENT_PLAYBOOK_KEYS = [
  'event_booth_meeting_v1',
  'event_session_invite_v1',
  'event_vip_outreach_v1',
  'event_quick_connect_v1',
];

/**
 * Static card copy when preview API has not been called yet.
 * `purpose` / `persona_scope` stay English (other callers read them as-is);
 * `*_key` fields are translation keys for render-time display only.
 */
export const EVENT_PLAYBOOK_DISPLAY = {
  event_booth_meeting_v1: {
    label: 'Booth Meeting',
    purpose: 'Drive booth visits and in-person demos.',
    purpose_key: 'pbBoothMeetingPurpose',
    persona_scope: 'Any persona',
    persona_scope_key: 'pbPersonaScopeAny',
    persona_scope_color: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: 'MapPin',
  },
  event_session_invite_v1: {
    label: 'Session Invite',
    purpose: 'Drive attendance to a speaking session.',
    purpose_key: 'pbSessionInvitePurpose',
    persona_scope: 'Any persona',
    persona_scope_key: 'pbPersonaScopeAny',
    persona_scope_color: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: 'Sparkles',
  },
  event_vip_outreach_v1: {
    label: 'VIP Outreach',
    purpose: '20 minutes with executive-level targets.',
    purpose_key: 'pbVipOutreachPurpose',
    persona_scope: 'Executive only',
    persona_scope_key: 'pbPersonaScopeExecutiveOnly',
    persona_scope_color: 'bg-amber-50 text-amber-800 border-amber-200',
    icon: 'Star',
  },
  event_quick_connect_v1: {
    label: 'Quick Connect',
    purpose: 'Lightweight peer outreach for confirmed attendees.',
    purpose_key: 'pbQuickConnectPurpose',
    persona_scope: 'Previous and confirmed attendees only',
    persona_scope_key: 'pbPersonaScopeConfirmedAttendees',
    persona_scope_color: 'bg-green-50 text-green-700 border-green-200',
    icon: 'ArrowUpCircle',
  },
};

export const VIP_OUTREACH_ARCHETYPES = new Set(['Economic Buyer', 'Champion']);

export function getEventPlaybookKey(playbook) {
  if (!playbook) return '';
  const raw = String(playbook.playbook_key || playbook.playbook_id || playbook.key || '').trim();
  if (raw.toLowerCase().startsWith('event::')) return raw;
  return normalizePlaybookKey(raw);
}

export function getEventPlaybookDisplay(playbookKey) {
  const pk = normalizePlaybookKey(playbookKey);
  return EVENT_PLAYBOOK_DISPLAY[pk] || null;
}

export function getEventConfirmedAttendeeCount(event) {
  if (!event || typeof event !== 'object') return 0;
  const n = Number(
    event.confirmed_attendees_count ??
      event.confirmed_attendee_count ??
      event.confirmed_count ??
      (Array.isArray(event.confirmed_attendees) ? event.confirmed_attendees.length : NaN),
  );
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function isQuickConnectPlaybookKey(playbookKey) {
  return normalizePlaybookKey(playbookKey) === 'event_quick_connect_v1';
}

export function isSessionInvitePlaybookKey(playbookKey) {
  return normalizePlaybookKey(playbookKey) === 'event_session_invite_v1';
}

/** Session Invite on Playbooks requires title + description from Settings → Events. */
export function hasEventSessionDetails(event) {
  if (!event || typeof event !== 'object') return false;
  const title = String(event.session_title ?? '').trim();
  const description = String(event.session_description ?? '').trim();
  return title.length > 0 && description.length > 0;
}

/** Whether Preview & Apply should be disabled on the Playbooks event cards. */
export function isEventPlaybookPreviewBlocked(playbookKey, selectedEventId, selectedEvent) {
  if (!selectedEventId) return true;
  if (isQuickConnectPlaybookKey(playbookKey)) {
    return getEventConfirmedAttendeeCount(selectedEvent) === 0;
  }
  if (isSessionInvitePlaybookKey(playbookKey)) {
    return !hasEventSessionDetails(selectedEvent);
  }
  return false;
}

/**
 * Whether this event + playbook type uses a custom sequence, defaults, or is missing per-type config.
 * @returns {{ mode: 'default'|'custom'|'not_configured'|'custom_legacy', label: string, description: string, stepCount?: number, badgeClass: string }}
 */
export function getEventCustomPlaybookStatusForType(event, playbookKey) {
  const pk = normalizePlaybookKey(playbookKey);
  const typeLabel = EVENT_PLAYBOOK_DISPLAY[pk]?.label || pk;

  if (!event?.use_custom_playbook) {
    return {
      mode: 'default',
      label: 'Default playbook',
      description: `Using the built-in ${typeLabel} sequence (not customized for this event).`,
      badgeClass: 'bg-slate-100 text-slate-700 border-slate-200',
    };
  }

  const byType = event.custom_touches_by_type;
  const typeTouches =
    byType && typeof byType === 'object' && !Array.isArray(byType) ? byType[pk] : null;
  const stepCount = Array.isArray(typeTouches) ? typeTouches.length : 0;

  if (stepCount > 0) {
    return {
      mode: 'custom',
      label: 'Custom playbook active',
      description: `${typeLabel} has ${stepCount} custom step${stepCount === 1 ? '' : 's'} from Settings → Events.`,
      stepCount,
      badgeClass: 'bg-purple-100 text-purple-800 border-purple-200',
    };
  }

  const flatCount = Array.isArray(event.custom_touches) ? event.custom_touches.length : 0;
  const hasAnyByType =
    byType &&
    typeof byType === 'object' &&
    !Array.isArray(byType) &&
    Object.values(byType).some((t) => Array.isArray(t) && t.length > 0);

  if (flatCount > 0 && !hasAnyByType) {
    return {
      mode: 'custom_legacy',
      label: 'Legacy custom sequence',
      description: `Event uses the older flat custom list (${flatCount} steps). Per-type ${typeLabel} config not set — backend may fall back to that list.`,
      stepCount: flatCount,
      badgeClass: 'bg-amber-100 text-amber-900 border-amber-200',
    };
  }

  return {
    mode: 'not_configured',
    label: 'Custom playbooks on — type not set',
    description: `${typeLabel} has no custom steps yet. Preview and runs use the default ${typeLabel} sequence until you configure it in Settings → Events.`,
    badgeClass: 'bg-amber-50 text-amber-900 border-amber-200',
  };
}

export function getEligibilityBlockMessage(reason) {
  if (reason === 'no_confirmed_attendees') {
    return 'Quick Connect requires confirmed event attendees. Mark at least one persona as attending below, or pick a different playbook.';
  }
  if (reason === 'missing_session_details') {
    return 'Session Invite requires a session title and description for this event. Add them under Session Details in Settings → Events, then return here.';
  }
  if (reason === 'no_personas_match_required_archetype') {
    return 'VIP Outreach is restricted to Economic Buyer and Champion archetypes. None of the personas at this event match.';
  }
  return 'This playbook is not eligible for the current audience.';
}
